import React, { useState, useEffect } from 'react';
import DOMPurify from "dompurify";

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// Componentes 
import SeleccionarRubro from './SeleccionarRubro';
import SeleccionarEspecialidades from './SeleccionarEspecialidades';
import SeleccionarPais from './SeleccionarPais';
import SeleccionarProvincia from './SeleccionarProvincia';
import SeleccionarLocalidad from './SeleccionarLocalidad';

// Quill components
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Estilo para el editor Quill

// Funciones para obtener datos
import ObtenerPreguntaFrecuente from '../functions/ObtenerPreguntaFrecuente';
import GuardarPreguntaFrecuente from '../functions/GuardarPreguntaFrecuente';
import InsertarPreguntaFrecuente from '../functions/InsertarPreguntaFrecuente';

import ObtenerPrestadorEspecialidades from '../functions/ObtenerPrestadorEspecialidades';
import GuardarEspecialidadesPrestador from '../functions/GuardarEspecialidadesPrestador';

// Tipos de datos
import PreguntaFrecuenteTabla from '../types/PreguntaFrecuenteTabla';

import PrestadorSeleccionar from '../types/PrestadorSeleccionar';
import EspecialidadSeleccionar from '../types/EspecialidadSeleccionar';
import PaisSeleccionar from '../types/PaisSeleccionar';
import ProvinciaSeleccionar from '../types/ProvinciaSeleccionar';
import LocalidadSeleccionar from '../types/LocalidadSeleccionar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';
import RubroSeleccionar from '../types/RubroSeleccionar';

interface EditarPreguntaFrecuenteProps {
    idPreguntaFrecuente: number;
    guardarCambios: (preguntafrecuente: PreguntaFrecuenteTabla) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}
  
export const EditarPreguntaFrecuente: React.FC<EditarPreguntaFrecuenteProps> = ({ idPreguntaFrecuente, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [preguntaFrecuente, setPreguntaFrecuente] = useState<PreguntaFrecuenteTabla | null>(null);
    
    const [errorPreguntaFrecuente, setErrorPreguntaFrecuente] = useState<string | null>(null);

    const [localPregunta, setLocalPregunta] = useState('');
    const [localRespuesta, setLocalRespuesta] = useState('');
    const [localOrden, setLocalOrden] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');

    useEffect(() => {
        
        const fetchPreguntaFrecuente = async () => {
            try {

                const data = await ObtenerPreguntaFrecuente(idPreguntaFrecuente);

                if (data.preguntafrecuente_ok === 'si') {

                    // Todo ok, prestador recuperado
                    const preguntaFrecuenteRecuperada: PreguntaFrecuenteTabla = {
                        id: data.preguntafrecuente[0].id,
                        pregunta: data.preguntafrecuente[0].pregunta,
                        respuesta: DOMPurify.sanitize(data.preguntafrecuente[0].respuesta),
                        habilitado: data.preguntafrecuente[0].habilitado,
                        fecha_creacion: data.preguntafrecuente[0].fecha_creacion,
                        fecha_modificacion: data.preguntafrecuente[0].fecha_modificacion,
                        dado_de_baja: data.preguntafrecuente[0].dado_de_baja,
                        fecha_baja: data.preguntafrecuente[0].fecha_baja,
                        orden: data.preguntafrecuente[0].orden
                    };

                    setPreguntaFrecuente(preguntaFrecuenteRecuperada);

                    setLocalPregunta(data.preguntafrecuente[0].pregunta);
                    setLocalRespuesta(DOMPurify.sanitize(data.preguntafrecuente[0].respuesta));
                    setLocalHabilitado(data.preguntafrecuente[0].habilitado);
                    setLocalFchCrea(data.preguntafrecuente[0].fecha_creacion);
                    setLocalFchModi(data.preguntafrecuente[0].fecha_modificacion);
                    setLocalDadoBaja(data.preguntafrecuente[0].dado_de_baja);
                    setLocalFchBaja(data.preguntafrecuente[0].fecha_baja);
                    setLocalOrden(data.preguntafrecuente[0].orden);

                    setErrorPreguntaFrecuente(null);

                } else {
                    
                    // Hubo errores al recuperar la pregunta frecuente
                    setErrorPreguntaFrecuente(data.mensaje);
                    setPreguntaFrecuente(null);
                    setLocalPregunta('');
                    setLocalRespuesta('');
                    setLocalHabilitado('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalFchBaja('');
                    setLocalDadoBaja('');
                    setLocalOrden('');
                }

                setIsLoading(false);
                                
            } catch (err) {
                setErrorPreguntaFrecuente('Error al obtener la información de la pregunta frecuente.');
                setPreguntaFrecuente(null);
                setLocalPregunta('');
                setLocalRespuesta('');
                setLocalHabilitado('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalFchBaja('');
                setLocalDadoBaja('');
                setLocalOrden('');
                setIsLoading(false);
            }
        };

        if (idPreguntaFrecuente > 0) {
            // Estoy editando una pregunta frecuente
            fetchPreguntaFrecuente();
        } else {
            // Estoy creando una nueva pregunta frecuente
            setIsLoading(false);
            setPreguntaFrecuente({id:0, pregunta: '', respuesta: '',  habilitado: '', fecha_creacion: '', fecha_modificacion: '', dado_de_baja: '', fecha_baja: '', orden: ''});
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
        }

    }, []);

    const handleChangeLocalPregunta = (event: React.ChangeEvent<HTMLInputElement>) => {
        //const pattern = /^[0-9]{0,11}$/;
        //if (pattern.test(event.target.value)) {
            setLocalPregunta(event.target.value);
        //}
    };

    const handleChangeLocalRespuesta = (value: string) => {
        setLocalRespuesta(DOMPurify.sanitize(value));
    };

    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    // Para actualizar las especialidades del prestador (ante cambios)
    const handleChangeLocalOrden = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^[0-9]{0,2}$/;
        if (pattern.test(event.target.value)) {
            setLocalOrden(event.target.value);
        }
    };

    
    const guardarCambiosLocal = async () => {

        if (idPreguntaFrecuente === 0) {
            // Crear nueva pregunta frecuente

            const PreguntaFrecuenteNueva: PreguntaFrecuenteTabla = {
                id: 0,
                pregunta: localPregunta,
                respuesta: DOMPurify.sanitize(localRespuesta),
                habilitado: localHabilitado,
                fecha_creacion: '',
                fecha_modificacion: '',
                dado_de_baja: 'N',
                fecha_baja: '',
                orden: localOrden
            };            

            const respuesta = await InsertarPreguntaFrecuente(PreguntaFrecuenteNueva);

            if (respuesta.preguntafrecuente_guardada === 'si') {
                guardarCambios(PreguntaFrecuenteNueva);
            } else {
                alert('Error al guardar los cambios: ' + respuesta.mensaje);
            }
            
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica de la pregunta frecuente
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos pregunta frecuente
                const PreguntaFrecuenteEditada: PreguntaFrecuenteTabla = {
                    id: idPreguntaFrecuente,
                    pregunta: localPregunta,
                    respuesta: localRespuesta,
                    habilitado: 'N',
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: 'S',
                    fecha_baja: guardarFecha,
                    orden: localOrden
                };

                const respuesta = await GuardarPreguntaFrecuente(PreguntaFrecuenteEditada);

                if (respuesta.preguntafrecuente_guardada === 'si') {
                    guardarCambios(PreguntaFrecuenteEditada);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar pregunta frecuente
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                
                // Modificar datos pregunta frecuente
                const PreguntaFrecuenteEditada: PreguntaFrecuenteTabla = {
                    id: idPreguntaFrecuente,
                    pregunta: localPregunta,
                    respuesta: localRespuesta,
                    habilitado: localHabilitado,
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: localDadoBaja,
                    fecha_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    orden: localOrden
                };

                const respuesta = await GuardarPreguntaFrecuente(PreguntaFrecuenteEditada);

                if (respuesta.preguntafrecuente_guardada === 'si') {
                    guardarCambios(PreguntaFrecuenteEditada);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
            }
        }
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && preguntaFrecuente) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Pregunta Frecuente: {preguntaFrecuente?.pregunta}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idPreguntaFrecuente > 0 ? "Editando Pregunta Frecuente: " + preguntaFrecuente?.pregunta : "Nueva Pregunta Frecuente"}</h1>
                    )}     
                    <div className="border-t-2 border-vp-azul w-full flex flex-col pt-3 justify-between items-start border-b-2 ">
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="pregunta" label="Pregunta" variant="outlined" value={localPregunta} autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalPregunta}
                                disabled={eliminando}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-start flex-grow w-full min-h-full">
                            <label className="font-bold">Respuesta</label>
                            <ReactQuill
                                theme="snow"
                                value={localRespuesta}
                                onChange={handleChangeLocalRespuesta}
                                placeholder="Escribe tu respuesta aquí..."
                                style={{width: "100%", height: '200px', scrollbarWidth: 'thin', marginBottom: '60px',}}
                                readOnly={eliminando}
                            />                                
                        </div>
                        <div className="flex flex-col justify-start items-center flex-grow w-full mt-6">
                            <div className="flex flex-row flex-wrap justify-between items-center flex-grow w-full">
                                {preguntaFrecuente.id > 0 && (
                                    <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                        <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                '& .MuiInputLabel-outlined': {
                                                    top: '-6px',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    top: '0px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            disabled
                                        />
                                    </div>)
                                }

                                <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                                    <FormControl fullWidth sx={{marginRight: '6px',}}>
                                        <InputLabel id="label-habilitado"
                                            sx={{top: '-6px',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, 0px) scale(0.75)',
                                                },
                                            }}
                                            >
                                                Habilitado
                                            </InputLabel>
                                        <Select
                                            labelId="label-habilitado"
                                            id="habilitado"
                                            value={localHabilitado}
                                            label="Habilitado"
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            onChange={handleChangeLocalHabitado}
                                            disabled={eliminando}
                                            >
                                            <MenuItem value={'S'}>Si</MenuItem>
                                            <MenuItem value={'N'}>No</MenuItem>
                                        </Select>
                                    </FormControl> 
                                </div>
                                <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                                    <FormControl fullWidth sx={{marginRight: '6px',}}>
                                        <InputLabel id="label-dadobaja"
                                            sx={{top: '-6px',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, 0px) scale(0.75)',
                                                },
                                            }}
                                            >
                                                Baja
                                            </InputLabel>
                                        <Select
                                            labelId="label-dadobaja"
                                            id="dadobaja"
                                            value={localDadoBaja}
                                            label="Baja"
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            onChange={handleChangeLocalDadoBaja}
                                            disabled={eliminando}
                                            >
                                            <MenuItem value={'S'}>Si</MenuItem>
                                            <MenuItem value={'N'}>No</MenuItem>
                                        </Select>
                                    </FormControl> 
                                </div>
                                {localDadoBaja === 'S' && (
                                    <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                        <TextField id="fchbaja" label="Fec. Baja" variant="outlined" value={localFchBaja}
                                            sx={{
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8px',
                                                },
                                                '& .MuiInputLabel-outlined': {
                                                    top: '-6px',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    top: '0px',
                                                },
                                                width: '100%',
                                                marginRight: '6px',
                                                marginBottom: '8px',
                                            }}
                                            disabled
                                        />
                                    </div>)
                                }
                                <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow">
                                    <TextField id="orden" label="Orden" variant="outlined" value={localOrden}
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px',
                                            },
                                            '& .MuiInputLabel-outlined': {
                                                top: '-6px',
                                            },
                                            '& .MuiInputLabel-shrink': {
                                                top: '0px',
                                            },
                                            width: '100%',
                                            marginRight: '6px',
                                            marginBottom: '8px',
                                        }}
                                        onChange={handleChangeLocalOrden}
                                        disabled={eliminando}
                                    />
                                </div>

                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};
